/**
 * Merge API `contacts` with legacy restaurant profile fields so the public contact section
 * shows email/phone/website/social the owner set outside Contact & links.
 * Dedupes when the same value already appears in `contacts`.
 */

import { contactValue, isLinkType } from '@/utils/contact'

/**
 * @param {{ contacts?: unknown[], phone?: string|null, email?: string|null, website?: string|null, social_links?: Record<string, string>|null }} restaurant
 * @returns {Array<{ uuid: string, type: string, value: string, number: string|null, label: string|null }>}
 */
export function mergeLegacyPublicContacts(restaurant) {
  const raw = restaurant && typeof restaurant === 'object' ? restaurant : {}
  const contacts = Array.isArray(raw.contacts) ? [...raw.contacts] : []
  const seen = new Set(contacts.map((c) => comparableKey(c)))

  /** @type {Array<{ uuid: string, type: string, value: string, number: string|null, label: string|null }>} */
  const out = contacts.map((c) => ({
    uuid: c.uuid != null ? String(c.uuid) : '',
    type: c.type ?? 'phone',
    value: contactValue(c),
    number: c.number != null ? String(c.number) : null,
    label: c.label ?? null,
  }))

  /**
   * @param {string} type
   * @param {string} value
   */
  function addIfNew(type, value, uuid) {
    const v = typeof value === 'string' ? value.trim() : ''
    if (v === '') return
    const key = comparableFromTypeValue(type, v)
    if (seen.has(key)) return
    seen.add(key)
    out.push({
      uuid,
      type,
      value: v,
      number: null,
      label: null,
    })
  }

  addIfNew('email', raw.email ?? '', 'legacy-profile-email')
  addIfNew('phone', raw.phone ?? '', 'legacy-profile-phone')

  const web = raw.website
  if (typeof web === 'string' && web.trim() !== '') {
    addIfNew('website', normalizeWebsiteUrl(web), 'legacy-profile-website')
  }

  const social = raw.social_links && typeof raw.social_links === 'object' ? raw.social_links : {}
  for (const key of ['facebook', 'instagram', 'twitter']) {
    const url = social[key]
    if (typeof url === 'string' && url.trim() !== '') {
      addIfNew(key, url.trim(), `legacy-social-${key}`)
    }
  }

  return out
}

/**
 * @param {{ type?: string, value?: string, number?: string }} c
 */
function comparableKey(c) {
  const type = c.type ?? 'phone'
  const v = contactValue(c)
  return comparableFromTypeValue(type, v)
}

/**
 * @param {string} type
 * @param {string} value
 */
function comparableFromTypeValue(type, value) {
  const v = (value || '').trim()
  if (type === 'phone' || type === 'mobile' || type === 'whatsapp' || type === 'viber' || type === 'fax' || type === 'other') {
    return `${type}:${v.replace(/\D/g, '')}`
  }
  if (type === 'email') {
    return `email:${v.toLowerCase()}`
  }
  if (isLinkType(type) || type === 'facebook' || type === 'instagram' || type === 'twitter') {
    return `${type}:${normalizeUrlComparable(v)}`
  }
  return `${type}:${v.toLowerCase()}`
}

/** Strip trailing slash and lowercase for loose URL dedupe. */
function normalizeUrlComparable(url) {
  try {
    const u = url.trim().toLowerCase()
    return u.replace(/\/+$/, '')
  } catch {
    return url.trim().toLowerCase()
  }
}

function normalizeWebsiteUrl(url) {
  const t = url.trim()
  if (/^https?:\/\//i.test(t)) return t
  return `https://${t}`
}
